import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import apiService from "../services/api";
import CounselorAssignmentManager from "../components/counselor-assignments/CounselorAssignmentManager";

const cardStyles = {
  card: { background: "white", borderRadius: "0.5rem", padding: "1.5rem", border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", transition: "transform 0.2s, box-shadow 0.2s" },
  cardName: { fontSize: "1.125rem", fontWeight: "600", color: "#001272", margin: "0 0 1rem" },
  row: { display: "flex", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: "1px solid #f1f5f9", fontSize: "0.875rem" },
  rowLabel: { color: "#001272", opacity: 0.7 },
  rowVal: { fontWeight: "500", color: "#001272", textAlign: "right", maxWidth: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  counselorBadge: { fontWeight: "500", color: "#166534" },
  noCounselor: { fontWeight: "500", color: "#9ca3af", fontStyle: "italic" },
  btn: { width: "100%", marginTop: "1rem", padding: "0.5rem 1rem", backgroundColor: "#001272", color: "white", border: "none", borderRadius: "0.375rem", fontSize: "0.875rem", fontWeight: "500", cursor: "pointer" },
};

const StudentCard = ({ student, onManage }) => {
  const fullName = student.displayName || `${student.firstName || ""} ${student.lastName || ""}`.trim() || "Unnamed Student";

  const counselorName = (student.currentCounselorFirstName || student.currentCounselorLastName)
    ? `${student.currentCounselorFirstName || ""} ${student.currentCounselorLastName || ""}`.trim()
    : null;

  return (
    <div
      style={cardStyles.card}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0,0,0,0.1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = cardStyles.card.boxShadow; }}
    >
      <h3 style={cardStyles.cardName}>{fullName}</h3>
      {student.email && (
        <div style={cardStyles.row}>
          <span style={cardStyles.rowLabel}>Email</span>
          <span style={cardStyles.rowVal}>{student.email}</span>
        </div>
      )}
      {student.branchId && (
        <div style={cardStyles.row}>
          <span style={cardStyles.rowLabel}>Branch</span>
          <span style={cardStyles.rowVal}>{student.branchId}</span>
        </div>
      )}
      {(student.payingClientFirstName || student.payingClientLastName) && (
        <div style={cardStyles.row}>
          <span style={cardStyles.rowLabel}>Paying Client</span>
          <span style={cardStyles.rowVal}>{`${student.payingClientFirstName || ""} ${student.payingClientLastName || ""}`.trim()}</span>
        </div>
      )}
      <div style={cardStyles.row}>
        <span style={cardStyles.rowLabel}>Current Counselor</span>
        {counselorName
          ? <span style={cardStyles.counselorBadge}>{counselorName}</span>
          : <span style={cardStyles.noCounselor}>None assigned</span>
        }
      </div>
      <button
        style={cardStyles.btn}
        onMouseEnter={(e) => { e.target.style.backgroundColor = "#000d5c"; }}
        onMouseLeave={(e) => { e.target.style.backgroundColor = "#001272"; }}
        onClick={() => onManage(student.id, fullName)}
      >
        Assign Counselor
      </button>
    </div>
  );
};

const CounselorsPage = () => {
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudentName, setSelectedStudentName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("students");

  const { data: students = [], isLoading, error } = useQuery({
    queryKey: ["students"],
    queryFn: () => apiService.getStudents(),
    staleTime: 5 * 60 * 1000,
  });

  const filtered = students.filter((st) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return `${st.firstName || ""} ${st.lastName || ""} ${st.email || ""}`.toLowerCase().includes(s);
  });

  const openModal = (studentId, studentName) => {
    setSelectedStudentId(studentId);
    setSelectedStudentName(studentName);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedStudentId(null);
    setSelectedStudentName("");
  };

  // Group students by counselor for the "By Counselor" tab
  const byCounselor = students.reduce((acc, st) => {
    const name = (st.currentCounselorFirstName || st.currentCounselorLastName)
      ? `${st.currentCounselorFirstName || ""} ${st.currentCounselorLastName || ""}`.trim()
      : null;
    const key = name || "__unassigned__";
    if (!acc[key]) acc[key] = { counselorName: name, students: [] };
    acc[key].students.push(st);
    return acc;
  }, {});

  const counselorGroups = Object.values(byCounselor).sort((a, b) => {
    if (!a.counselorName) return 1;
    if (!b.counselorName) return -1;
    return a.counselorName.localeCompare(b.counselorName);
  });

  const s = {
    page: { padding: "1rem 0" },
    header: { marginBottom: "1.5rem" },
    h1: { fontSize: "1.875rem", fontWeight: "700", color: "#001272", marginBottom: "0.5rem" },
    subtitle: { color: "#001272", opacity: "0.8", fontSize: "1rem" },
    tabs: { display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: "1.5rem" },
    tab: { padding: "0.6rem 1.25rem", fontSize: "0.875rem", fontWeight: "500", cursor: "pointer", border: "none", background: "none", color: "#64748b", borderBottom: "2px solid transparent", marginBottom: "-2px" },
    tabActive: { color: "#001272", borderBottomColor: "#001272", fontWeight: "600" },
    controls: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" },
    search: { padding: "0.5rem 1rem", border: "1px solid #d1d5db", borderRadius: "0.375rem", fontSize: "0.875rem", minWidth: "300px", outline: "none" },
    count: { color: "#6b7280", fontSize: "0.875rem" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1.5rem" },
    emptyState: { textAlign: "center", padding: "3rem 1rem", color: "#6b7280" },
    loadingWrap: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "4rem 0" },
    spinner: { border: "3px solid #f3f3f3", borderTop: "3px solid #53bdeb", borderRadius: "50%", width: "40px", height: "40px", animation: "spin 1s linear infinite", marginBottom: "1rem" },
    errBox: { background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "0.375rem", padding: "1rem", color: "#991b1b" },
    groupCard: { background: "white", borderRadius: "0.5rem", border: "1px solid #e2e8f0", marginBottom: "1rem", overflow: "hidden" },
    groupHeader: { padding: "0.75rem 1rem", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: "0.75rem" },
    groupName: { fontWeight: "600", color: "#001272", fontSize: "0.95rem" },
    groupCount: { fontSize: "0.75rem", color: "#6b7280", background: "#e2e8f0", borderRadius: "9999px", padding: "0.1rem 0.5rem" },
    studentRow: { padding: "0.6rem 1rem", borderBottom: "1px solid #f1f5f9", fontSize: "0.875rem", display: "flex", justifyContent: "space-between", alignItems: "center", color: "#374151" },
  };

  if (isLoading) return (
    <div style={s.page}>
      <div style={s.header}><h1 style={s.h1}>Counselor Management</h1></div>
      <div style={s.loadingWrap}>
        <div style={s.spinner} />
        <p style={{ color: "#001272" }}>Loading students...</p>
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={s.page}>
      <div style={s.header}><h1 style={s.h1}>Counselor Management</h1></div>
      <div style={s.errBox}>Error loading students: {error.message}</div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.h1}>Counselor Management</h1>
        <p style={s.subtitle}>Assign and manage counselors for students.</p>
        <p style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "0.35rem", margin: "0.35rem 0 0 0" }}>
          Students are synced from TC only once daily around 1:00 AM America/Los_Angeles time.
        </p>
      </div>

      <div style={s.tabs}>
        <button
          style={{ ...s.tab, ...(activeTab === "students" ? s.tabActive : {}) }}
          onClick={() => setActiveTab("students")}
        >
          By Student
        </button>
        <button
          style={{ ...s.tab, ...(activeTab === "counselors" ? s.tabActive : {}) }}
          onClick={() => setActiveTab("counselors")}
        >
          By Counselor
        </button>
      </div>

      {activeTab === "students" ? (
        <>
          <div style={s.controls}>
            <input
              style={s.search}
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span style={s.count}>Showing {filtered.length} of {students.length} students</span>
          </div>

          {filtered.length === 0 ? (
            <div style={s.emptyState}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "0.5rem" }}>No students found</h3>
              <p style={{ fontSize: "0.875rem" }}>
                {searchTerm
                  ? "No students match your search."
                  : "No students available yet. Students will appear once synced from TutorCruncher."}
              </p>
            </div>
          ) : (
            <div style={s.grid}>
              {filtered.map((student) => (
                <StudentCard key={student.id} student={student} onManage={openModal} />
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <div style={s.controls}>
            <span style={s.count}>
              {counselorGroups.filter(g => g.counselorName).length} counselors · {students.length} students
            </span>
          </div>

          {counselorGroups.map((group) => (
            <div key={group.counselorName || "__unassigned__"} style={s.groupCard}>
              <div style={s.groupHeader}>
                {group.counselorName
                  ? <span style={s.groupName}>{group.counselorName}</span>
                  : <span style={{ ...s.groupName, color: "#9ca3af", fontStyle: "italic" }}>No Counselor Assigned</span>}
                <span style={s.groupCount}>{group.students.length}</span>
              </div>
              <div>
                {group.students
                  .slice()
                  .sort((a, b) => {
                    const nameA = (a.displayName || `${a.firstName || ""} ${a.lastName || ""}`).trim().toLowerCase();
                    const nameB = (b.displayName || `${b.firstName || ""} ${b.lastName || ""}`).trim().toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                  .map((st) => {
                    const name = st.displayName || `${st.firstName || ""} ${st.lastName || ""}`.trim() || "Unnamed";
                    return (
                      <div key={st.id} style={s.studentRow}>
                        <span>{name}</span>
                        {st.email && <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>{st.email}</span>}
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </>
      )}

      {isModalOpen && selectedStudentId && (
        <CounselorAssignmentManager
          studentId={selectedStudentId}
          studentName={selectedStudentName}
          onClose={closeModal}
        />
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CounselorsPage;
